import api from './api'

export type BackfillAuditStatus =
  | 'identical'
  | 'differs'
  | 'missing_in_qbo'
  | 'duplicate_in_qbo'
  | 'rebuild_failed'
  | 'no_activity'

export type BackfillAuditDecision =
  | 'undecided'
  | 'auto'
  | 'by_hand'
  | 'done_by_hand'
  | 'skip'
  | 'needs_code_change'
  | 'closed_year'
  | 'applied'

export type BackfillAuditCategory =
  | 'gift_card_only'
  | 'deposit_window'
  | 'deposit_mismatch'
  | 'sales_tax_tips'
  | 'ar_classification'
  | 'manual_correction'
  | 'processor_unavailable'
  | 'unbalanced_rebuild'
  | 'date_differs'
  | 'doc_number_legacy'

export interface BackfillAuditBounds {
  closed_through: string
  catch_up_txn_date: string
  updatable_from: string
  updatable_through: string | null
  deposit_report_cutoff: string
}

export interface BackfillAuditListItem {
  store_name: string
  report_date: string
  status: BackfillAuditStatus
  categories: string[]
  qbo_je_id: string | null
  qbo_doc_number: string | null
  qbo_txn_date: string | null
  delta_debits: number | string | null
  diff_summary: string | null
  suggested_decision: BackfillAuditDecision | null
  decision: BackfillAuditDecision
  decision_note: string | null
  processor_report_available: boolean | null
  deposit_matches: string | null
  deposit_window_probe_matches?: string | null
}

export interface BackfillAuditListResponse {
  items: BackfillAuditListItem[]
  total: number
  page: number
  page_size: number
}

export interface BackfillAuditLineDiffChanged {
  key?: string
  account_name?: string
  dept_name?: string
  qbo_net?: number | string
  rebuilt_net?: number | string
  delta?: number | string
}

export interface BackfillAuditLineDiff {
  changed?: BackfillAuditLineDiffChanged[]
  only_in_qbo?: unknown[]
  only_in_rebuilt?: unknown[]
  totals?: Record<string, number | string>
  description_only_changes?: number
}

export interface BackfillAuditNormLine {
  account_id?: string
  account_name?: string
  department_id?: string | null
  department_name?: string | null
  entity_id?: string | null
  entity_name?: string | null
  posting_type?: string
  amount?: number | string
  description?: string
}

export interface BackfillAuditDetail extends BackfillAuditListItem {
  rebuilt_payload?: Record<string, unknown> | null
  qbo_lines?: BackfillAuditNormLine[]
  diff?: BackfillAuditLineDiff | null
  deposit_window_probe?: Record<string, unknown> | null
  rebuilt_warnings?: string[]
  processor_rows?: Record<string, unknown>[] | null
  existing_draft_id?: number | null
}

export interface BackfillAuditSummaryMonth {
  month: string
  identical: number
  differs: number
  no_activity: number
  by_category: Record<string, number>
  by_decision: Record<string, number>
  undecided: number
  gift_card_delta: number | string
}

export interface BackfillAuditSummary {
  months: BackfillAuditSummaryMonth[]
  deposit_cutoff_inferred: string | null
  deposit_report_cutoff: string
  cutoff_disagrees: boolean
  other_match_range: string | null
}

export interface BackfillAuditRunRequest {
  store_names?: string[]
  from?: string
  to?: string
  only_undecided?: boolean
  refresh?: boolean
}

export interface BackfillAuditRunResponse {
  job_id: number
}

export interface BackfillAuditDecisionRequest {
  decision: BackfillAuditDecision
  decision_note?: string | null
}

export interface BackfillAuditBulkDecisionRequest {
  decision: BackfillAuditDecision
  decision_note?: string | null
  from: string
  to: string
  store?: string
  status?: string
  category?: string
}

export interface BackfillAuditBulkDecisionResponse {
  updated: number
  skipped_closed_year: number
}

export interface CatchUpProposalLine {
  account_id: string
  account_name: string
  department?: string | null
  department_id?: string | null
  entity?: string | null
  entity_id?: string | null
  posting_type: 'Debit' | 'Credit'
  amount: number | string
  reason: string
}

export interface CatchUpProposalDriver {
  reason?: string
  amount?: number | string
  row_count?: number
  [key: string]: unknown
}

export interface CatchUpProposalExclusion {
  store_name?: string
  report_date?: string
  status?: string
  categories?: string[]
  reason?: string
}

export interface CatchUpDroppedPnl {
  account_id?: string
  account_name?: string
  net?: number | string
}

export interface CatchUpProposal {
  needed: boolean
  balanced: boolean
  txn_date: string
  message: string | null
  lines: CatchUpProposalLine[]
  drivers: CatchUpProposalDriver[]
  dropped_pnl: CatchUpDroppedPnl[]
  liability_residual: number | string | null
  liability_nets_to_zero: boolean
  exclusions: CatchUpProposalExclusion[]
  warnings: string[]
  prior_year_adjustment_mapped: boolean
}

export interface BackfillAuditListParams {
  from?: string
  to?: string
  store?: string
  status?: string
  category?: string
  decision?: string
  page?: number
  page_size?: number
}

const BASE = '/clover-reports/backfill-audit'

export function qboJournalUrl(jeId: string): string {
  return `https://app.qbo.intuit.com/app/journal?txnId=${encodeURIComponent(jeId)}`
}

export async function getBackfillAuditBounds(): Promise<BackfillAuditBounds> {
  const { data } = await api.get<BackfillAuditBounds>(`${BASE}/bounds`)
  return data
}

export async function runBackfillAudit(
  body: BackfillAuditRunRequest,
): Promise<BackfillAuditRunResponse> {
  const { data } = await api.post<BackfillAuditRunResponse>(`${BASE}/run`, body)
  return data
}

function normalizeListItem(item: BackfillAuditListItem): BackfillAuditListItem {
  return {
    ...item,
    deposit_matches: item.deposit_matches ?? item.deposit_window_probe_matches ?? null,
  }
}

export async function listBackfillAudit(
  params: BackfillAuditListParams,
): Promise<BackfillAuditListResponse> {
  const { data } = await api.get<BackfillAuditListResponse>(BASE, { params })
  return {
    ...data,
    items: (data.items ?? []).map(normalizeListItem),
  }
}

export async function getBackfillAuditDetail(
  store: string,
  date: string,
): Promise<BackfillAuditDetail> {
  const { data } = await api.get<BackfillAuditDetail>(
    `${BASE}/${encodeURIComponent(store)}/${encodeURIComponent(date)}`,
  )
  const payload = (data.rebuilt_payload ?? {}) as Record<string, unknown>
  const sourceData = (payload.source_data ?? {}) as Record<string, unknown>
  const activity = sourceData.gift_card_activity as Record<string, unknown> | undefined
  const warnings = data.rebuilt_warnings
    ?? (Array.isArray(payload.warnings) ? (payload.warnings as string[]) : [])
  const processorRows =
    data.processor_rows
    ?? (Array.isArray(activity?.rows) ? (activity.rows as Record<string, unknown>[]) : null)
    ?? (activity ? [activity] : null)
  return {
    ...normalizeListItem(data),
    rebuilt_warnings: warnings,
    processor_rows: processorRows,
  }
}

export async function updateBackfillAuditDecision(
  store: string,
  date: string,
  body: BackfillAuditDecisionRequest,
): Promise<BackfillAuditDetail> {
  const { data } = await api.put<BackfillAuditDetail>(
    `${BASE}/${encodeURIComponent(store)}/${encodeURIComponent(date)}/decision`,
    body,
  )
  return data
}

export async function bulkUpdateBackfillAuditDecision(
  body: BackfillAuditBulkDecisionRequest,
): Promise<BackfillAuditBulkDecisionResponse> {
  const { data } = await api.put<BackfillAuditBulkDecisionResponse>(`${BASE}/bulk-decision`, body)
  return data
}

type BackendSummaryMonthBucket = {
  status_counts?: Record<string, number>
  category_counts?: Record<string, number>
  decision_counts?: Record<string, number>
  gift_card_delta?: number | string
}

type BackendSummaryResponse = BackfillAuditSummary & {
  by_month?: Record<string, BackendSummaryMonthBucket>
  deposit_cutoff?: {
    inferred_cutoff?: string | null
    compared_cutoff?: string
    cutoff_disagrees?: boolean
    first_other_match?: string
    last_other_match?: string
  }
  total_rows?: number
}

function normalizeSummary(raw: BackendSummaryResponse): BackfillAuditSummary {
  const cutoff = raw.deposit_cutoff ?? {}
  const otherRange =
    raw.other_match_range ??
    (cutoff.first_other_match && cutoff.last_other_match
      ? `${cutoff.first_other_match} → ${cutoff.last_other_match}`
      : null)
  if (Array.isArray(raw.months) && raw.months.length > 0) {
    return {
      months: raw.months,
      deposit_cutoff_inferred:
        raw.deposit_cutoff_inferred ?? cutoff.inferred_cutoff ?? null,
      deposit_report_cutoff:
        raw.deposit_report_cutoff || cutoff.compared_cutoff || '',
      cutoff_disagrees: raw.cutoff_disagrees ?? Boolean(cutoff.cutoff_disagrees),
      other_match_range: otherRange,
    }
  }
  const byMonth = raw.by_month ?? {}
  const months: BackfillAuditSummaryMonth[] = Object.entries(byMonth)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([month, bucket]) => ({
      month,
      identical: bucket.status_counts?.identical ?? 0,
      differs: bucket.status_counts?.differs ?? 0,
      no_activity: bucket.status_counts?.no_activity ?? 0,
      by_category: bucket.category_counts ?? {},
      by_decision: bucket.decision_counts ?? {},
      undecided: bucket.decision_counts?.undecided ?? 0,
      gift_card_delta: bucket.gift_card_delta ?? 0,
    }))
  return {
    months,
    deposit_cutoff_inferred: cutoff.inferred_cutoff ?? null,
    deposit_report_cutoff: cutoff.compared_cutoff ?? raw.deposit_report_cutoff ?? '',
    cutoff_disagrees: Boolean(cutoff.cutoff_disagrees),
    other_match_range: otherRange,
  }
}

export async function getBackfillAuditSummary(
  params: { from?: string; to?: string },
): Promise<BackfillAuditSummary> {
  const { data } = await api.get<BackendSummaryResponse>(`${BASE}/summary`, { params })
  return normalizeSummary(data)
}

export async function getCatchUpProposal(): Promise<CatchUpProposal> {
  const { data } = await api.get<CatchUpProposal>(`${BASE}/catch-up-proposal`)
  return data
}

export async function downloadBackfillAuditCsv(
  params: Omit<BackfillAuditListParams, 'page' | 'page_size'>,
): Promise<void> {
  const response = await api.get<Blob>(`${BASE}/export.csv`, {
    params,
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] as string | undefined
  let filename = 'clover-backfill-audit.csv'
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition)
    if (match?.[1]) filename = match[1]
  }
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}
